import { getReconDetailsRoute } from '@src/containers/Routes/routes';
import { useCurrentAppLayoutId } from '@src/hooks/useAppLayoutId';
import { useResolveReconMismatchesDrawer } from '@src/hooks/useResolveReconMismatchesDrawer';
import {
  IconName,
  MixpanelEvent,
  MixpanelEventProperty,
  MixpanelEventSource,
  ReconStateEnum,
  filterByCellValueMenuItem,
  getRowNodesToOperateOn,
  includesEnumValue,
  useCallbackRef,
  useMixpanel,
  type AgGridMenuItems,
  type FilterableProperty,
  type ReconMismatch,
  type UseFilterBuilderOutput,
} from '@talos/kyoko';
import type { GetContextMenuItemsParams } from 'ag-grid-community';
import { compact } from 'lodash-es';
import { useNavigate } from 'react-router-dom';
import { colIDToFilterBuilderKey } from './useReconMismatchesFilter';

const RESOLVABLE_STATES = [ReconStateEnum.PendingReview];

export const useResolveMismatchMenu = ({
  openClause,
  filterableProperties,
}: {
  openClause: UseFilterBuilderOutput['addAndOpenClause'];
  filterableProperties: FilterableProperty<string>[];
}): {
  getCustomContextMenuItems: (params: GetContextMenuItemsParams<ReconMismatch>) => AgGridMenuItems;
} => {
  const mixpanel = useMixpanel();
  const { openResolveReconMismatchesDrawer } = useResolveReconMismatchesDrawer();
  const navigate = useNavigate();
  const layoutId = useCurrentAppLayoutId();

  const getCustomContextMenuItems = useCallbackRef((params: GetContextMenuItemsParams<ReconMismatch>) => {
    const selectedMismatches = compact(getRowNodesToOperateOn(params).map(node => node.data));
    const resolvableSelectedMismatches = selectedMismatches.filter(mismatch =>
      includesEnumValue(RESOLVABLE_STATES, mismatch.State)
    );

    return compact([
      ...filterByCellValueMenuItem({
        params,
        filterableProperties,
        openClause,
        colIDToFilterBuilderKey,
        mixpanel,
      }),
      selectedMismatches.length > 0
        ? {
            name: `Open Details (${selectedMismatches.length})`,
            action: () => {
              const mismatchIDs = selectedMismatches.map(m => m.ID);
              navigate(getReconDetailsRoute({ mismatchIDs, layoutId }));
              mixpanel.track(MixpanelEvent.CreateTempReconMismatchDetailsTab, {
                [MixpanelEventProperty.Amount]: mismatchIDs.length,
              });
            },
            icon: `<i class="ag-icon ${IconName.Deepdive}"/>`,
          }
        : undefined,
      resolvableSelectedMismatches.length > 0
        ? {
            name: `Resolve (${resolvableSelectedMismatches.length})`,
            action: () => {
              openResolveReconMismatchesDrawer(
                resolvableSelectedMismatches.map(m => m.ID),
                MixpanelEventSource.ReconMismatchesBlotter
              );
            },
            icon: `<i class="ag-icon ${IconName.Pencil}"/>`,
          }
        : undefined,
    ]);
  });

  return {
    getCustomContextMenuItems,
  };
};
